import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  NumberInput,
  NumberInputField,
  HStack,
  useToast,
  Container,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { ethers } from 'ethers';
import { useAppKitAccount, useAppKitProvider, useAppKitNetwork, useDisconnect, useAppKit } from '@reown/appkit/react';
import { polygon, mainnet } from '@reown/appkit/networks';
import { config } from './config';
import ERCABI from './erc20.json';
import NativeBitBitWin from './NativeBitBitWin.json';
import ERCBitBitWin from './ERCBitBitWin.json'
import { WalletInfo, PoolSelector, convertTokenAmt, parseTokenAmt } from './App';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';

function DepositModal({ isOpen, onClose, pool, balance, walletProvider, account, chainConfig, refreshBalance }) {
  const [amount, setAmount] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isDepositing, setIsDepositing] = useState(false);
  const [transactionSteps, setTransactionSteps] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [txError, setTxError] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setTxError('');
      setShowTransactions(false);
      if (pool?.token) {
        setTransactionSteps([
          { id: 'approve', label: 'Approve Token Spending', status: 'not_started' },
          { id: 'deposit', label: 'Deposit to Pool', status: 'not_started' }
        ]);
      } else {
        setTransactionSteps([]);
      }
    }
  }, [isOpen, pool]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'not_started':
        return <Box w="4" h="4" borderRadius="full" bg="gray.500" />;
      case 'in_progress':
        return <Spinner size="sm" color="blue.400" />;
      case 'succeed':
        return <Box color="green.400"><CheckIcon /></Box>;
      case 'failed':
        return <Box color="red.400"><CloseIcon /></Box>;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'not_started':
        return 'gray.500';
      case 'in_progress':
        return 'blue.400';
      case 'succeed':
        return 'green.400';
      case 'failed':
        return 'red.400';
      default:
        return 'gray.400';
    }
  };

  const handleDeposit = async () => {
    if (!amount) return;
    const amountWei = parseTokenAmt(amount, pool);
    setTxError('');
    setShowTransactions(true);

    try {
      if (pool.token) {
        // Need approval first
        setIsApproving(true);
        setTransactionSteps(steps =>
          steps.map(step => step.id === 'approve' ? { ...step, status: 'in_progress' } : step)
        );

        const provider = new ethers.BrowserProvider(walletProvider);
        const signer = await provider.getSigner();
        let writeContract = new ethers.Contract(
          pool.token,
          ERCABI,
          signer
        );
        let ercContract = new ethers.Contract(
          pool.token,
          ERCABI,
          chainConfig.provider
        );

        const allowance = await ercContract.allowance(account, pool.address);

        if (allowance < amountWei) {
          if (pool.zeroAllowance && allowance !== 0n) {
            const tx = await writeContract.approve(pool.address, 0);
            await tx.wait();
          }
          const tx = await writeContract.approve(pool.address, amountWei);
          await tx.wait();
        }
        setIsApproving(false);
        setTransactionSteps(steps =>
          steps.map(step => step.id === 'approve' ? { ...step, status: 'succeed' } : step)
        );
      }

      // Perform deposit
      setIsDepositing(true);
      setTransactionSteps(steps =>
        steps.map(step => step.id === 'deposit' ? { ...step, status: 'in_progress' } : step)
      );

      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(
        pool.address,
        NativeBitBitWin.abi,
        signer
      );
      if (pool.token) {
        const tx = await contract.deposit(amountWei);
        await tx.wait();
      } else {
        const tx = await contract.deposit(amountWei, { value: amountWei });
        await tx.wait();
      }

      setTransactionSteps(steps =>
        steps.map(step => step.id === 'deposit' ? { ...step, status: 'succeed' } : step)
      );

      toast({
        title: 'Deposit successful',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      refreshBalance();
      onClose();
    } catch (error) {
      console.error('Deposit error:', error);
      const failedStep = isApproving ? 'approve' : 'deposit';
      setTransactionSteps(steps =>
        steps.map(step => step.id === failedStep ? { ...step, status: 'failed' } : step)
      );
      setTxError(error.shortMessage || error.message);
      toast({
        title: 'Deposit failed',
        description: error.shortMessage || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDepositing(false);
      setIsApproving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isDepositing || isApproving ? undefined : onClose}
      closeOnOverlayClick={!isDepositing && !isApproving}
      closeOnEsc={!isDepositing && !isApproving}
      isCentered
      size="xl"
      blockScrollOnMount={false}
      touchStartCapture={false}
      touchMoveCapture={false}
    >
      <ModalOverlay />
      <ModalContent bg="gray.800" border="1px" borderColor="blue.500">
        <ModalHeader
          bgGradient="linear(to-r, cyan.400, blue.500)"
          bgClip="text"
          fontSize="2xl"
        >
          Deposit {pool?.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6}>
            <Box w="100%">
              <Text color="gray.300" mb={2}>Amount to deposit:</Text>
              <Box
                position="relative"
                bg="gray.800"
                borderRadius="lg"
                p={1}
                borderWidth="1px"
                borderColor="blue.500"
                _hover={{
                  borderColor: "blue.400",
                  boxShadow: "0 0 20px rgba(66, 153, 225, 0.2)"
                }}
              >
                <NumberInput
                  value={amount}
                  onChange={(value) => setAmount(value)}
                  max={balance}
                  min={0}
                  precision={pool?.displayDecimals || 6}
                  size="lg"
                >
                  <NumberInputField
                    placeholder="Enter amount to deposit"
                    bg="transparent"
                    color="white"
                    fontSize="xl"
                    border="none"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    type="number"
                    step="any"
                    data-form-type="other"
                    _focus={{
                      border: "none",
                      boxShadow: "none"
                    }}
                    _hover={{
                      border: "none"
                    }}
                  />
                </NumberInput>
                <Text
                  position="absolute"
                  right="4"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                  fontSize="lg"
                  pointerEvents="none"
                >
                  {pool?.name}
                </Text>
              </Box>
              <HStack justify="flex-end" mt={2} spacing={1}>
                <Text color="gray.400" fontSize="sm">Available:</Text>
                <Text
                  color="blue.400"
                  fontSize="sm"
                  cursor="pointer"
                  onClick={() => setAmount(balance)}
                  _hover={{ color: "blue.300" }}
                >
                  {Number(balance).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: pool?.displayDecimals || 6
                  })} {pool?.name}
                </Text>
              </HStack>
            </Box>

            {/* Transaction Steps - only show for pools with token property */}
            {pool?.token && showTransactions && (
              <Box
                w="100%"
                p={4}
                borderRadius="xl"
                bg="gray.700"
                borderWidth="1px"
                borderColor="blue.500"
                boxShadow="0 0 20px rgba(66, 153, 225, 0.2)"
              >
                <VStack align="stretch" spacing={3}>
                  <Text color="white" fontWeight="bold">Transaction Progress:</Text>
                  {transactionSteps.map((step, index) => (
                    <HStack key={step.id} spacing={3} p={2} bg="gray.800" borderRadius="md">
                      {getStatusIcon(step.status)}
                      <Text
                        color={getStatusColor(step.status)}
                        fontWeight="medium"
                      >
                        {step.label}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}

            {txError && (
              <Alert status="error" bg="red.900" borderRadius="md">
                <AlertIcon />
                <Text color="white">{txError}</Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            color="gray.300"
            mr={3}
            onClick={onClose}
            isDisabled={isDepositing || isApproving}
            _hover={{
              bg: 'whiteAlpha.100'
            }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="yellow"
            onClick={handleDeposit}
            isLoading={isDepositing || isApproving}
            loadingText={isApproving ? "Approving..." : "Depositing..."}
            spinnerPlacement="start"
            isDisabled={!amount || amount <= 0 || amount > balance || isDepositing || isApproving}
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '0 0 20px rgba(236, 201, 75, 0.3)',
            }}
          >
            Deposit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function WithdrawModal({ isOpen, onClose, pool, balance, walletProvider, account, chainConfig, refreshBalance }) {
  const [amount, setAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [txError, setTxError] = useState('');
  const [showTransactions, setShowTransactions] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setTxError('');
      setShowTransactions(false);
    }
  }, [isOpen]);

  const handleWithdraw = async () => {
    if (!amount) return;
    const amountWei = parseTokenAmt(amount, pool);
    setTxError('');
    setShowTransactions(true);

    try {
      setIsWithdrawing(true);
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(
        pool.address,
        NativeBitBitWin.abi,
        signer
      );
      const tx = await contract.withdraw(amountWei);
      await tx.wait();

      toast({
        title: 'Withdrawal successful',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      refreshBalance();
      onClose();
    } catch (error) {
      console.error('Withdrawal error:', error);
      setTxError(error.shortMessage || error.message);
      toast({
        title: 'Withdrawal failed',
        description: error.shortMessage || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsWithdrawing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isWithdrawing ? undefined : onClose}
      closeOnOverlayClick={!isWithdrawing}
      closeOnEsc={!isWithdrawing}
      isCentered
      size="xl"
      blockScrollOnMount={false}
      touchStartCapture={false}
      touchMoveCapture={false}
    >
      <ModalOverlay />
      <ModalContent bg="gray.800" border="1px" borderColor="blue.500">
        <ModalHeader color="white">Withdraw {pool?.poolTokenSymbol}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6}>
            <Box w="100%">
              <Text color="gray.300" mb={2}>Amount to withdraw:</Text>
              <Box
                position="relative"
                bg="gray.800"
                borderRadius="lg"
                p={1}
                borderWidth="1px"
                borderColor="blue.500"
                _hover={{
                  borderColor: "blue.400",
                  boxShadow: "0 0 20px rgba(66, 153, 225, 0.2)"
                }}
              >
                <NumberInput
                  value={amount}
                  onChange={(value) => setAmount(value)}
                  max={balance}
                  min={0}
                  precision={pool?.displayDecimals || 6}
                  size="lg"
                >
                  <NumberInputField
                    placeholder="Enter amount to withdraw"
                    bg="transparent"
                    color="white"
                    fontSize="xl"
                    border="none"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    type="number"
                    step="any"
                    data-form-type="other"
                    _focus={{
                      border: "none",
                      boxShadow: "none"
                    }}
                    _hover={{
                      border: "none"
                    }}
                  />
                </NumberInput>
                <Text
                  position="absolute"
                  right="4"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                  fontSize="lg"
                  pointerEvents="none"
                >
                  {pool?.poolTokenSymbol}
                </Text>
              </Box>
              <HStack justify="flex-end" mt={2} spacing={1}>
                <Text color="gray.400" fontSize="sm">Available:</Text>
                <Text
                  color="blue.400"
                  fontSize="sm"
                  cursor="pointer"
                  onClick={() => setAmount(balance)}
                  _hover={{ color: "blue.300" }}
                >
                  {Number(balance).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: pool?.displayDecimals || 6
                  })} {pool?.poolTokenSymbol}
                </Text>
              </HStack>
            </Box>

            {txError && (
              <Alert status="error" bg="red.900" borderRadius="md">
                <AlertIcon />
                <Text color="white">{txError}</Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            color="gray.300"
            mr={3}
            onClick={onClose}
            isDisabled={isWithdrawing}
            _hover={{
              bg: 'whiteAlpha.100'
            }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="yellow"
            onClick={handleWithdraw}
            isLoading={isWithdrawing}
            loadingText="Withdrawing..."
            spinnerPlacement="start"
            isDisabled={!amount || amount <= 0 || amount > balance || isWithdrawing}
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '0 0 20px rgba(236, 201, 75, 0.3)',
            }}
          >
            Withdraw
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function PoolPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { address, status } = useAppKitAccount();
  const account = address;
  const appkitProvider = useAppKitProvider('eip155');
  console.log('appkitProvider', appkitProvider);
  const { walletProvider,walletProviderType } = useAppKitProvider('eip155');
  const { chainId, switchNetwork } = useAppKitNetwork();
  const { disconnect } = useDisconnect();
  const { open: openConnectModal } = useAppKit();
  const toast = useToast();
  const chainConfig = config[chainId];
  useEffect(() => {
    if(walletProviderType && !walletProvider && account && status === 'connected') {
      toast({
        title: 'Wallet connecting in progress',
        description: 'Auto reconnecting to wallet',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  },[walletProvider,walletProviderType]);
  const handleSwitchChain = async () => {
    try {
      const networks = [
        polygon,
        mainnet
      ];

      const currentIndex = networks.findIndex(n => n.id === chainId);
      const nextNetwork = networks[(currentIndex + 1) % networks.length];
      console.log('currentIndex', currentIndex, 'nextNetwork', nextNetwork);
      await switchNetwork(nextNetwork);

      toast({
        title: 'Network Changed',
        description: `Switched to ${nextNetwork.name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error Switching Network',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const [selectedPool, setSelectedPool] = useState(null);
  const [userPoolBalance, setUserPoolBalance] = useState(0);
  const [userUnderlyingBalance, setUserUnderlyingBalance] = useState(0);
  const [poolUnderlyingBalance, setPoolUnderlyingBalance] = useState(0);
  const [poolPrice, setPoolPrice] = useState(null);
  const { isOpen: isDepositOpen, onOpen: onDepositOpen, onClose: onDepositClose } = useDisclosure();
  const { isOpen: isWithdrawOpen, onOpen: onWithdrawOpen, onClose: onWithdrawClose } = useDisclosure();

  const pool = chainConfig?.pools?.find(p => p?.name === selectedPool);
  const updateSelectedPool = (newPool) => {
    console.log('set selected pool', newPool);
    setUserPoolBalance(0);
    setUserUnderlyingBalance(0);
    setPoolUnderlyingBalance(0);
    setPoolPrice(0);
    setSelectedPool(newPool);
  };

  useEffect(() => {
    if (chainConfig && !pool && chainConfig.pools && chainConfig.pools.length) {
      setSelectedPool(chainConfig.pools[0].name);
    }
  }, [chainConfig, pool]);
  const refreshBalance=async () => {
    if (pool && selectedPool && account) {
      if (pool.token) {
        const ercUnderlyingContract = new ethers.Contract(
          pool.token,
          ERCABI,
          chainConfig.provider
        );
        const underlyingBalance = await ercUnderlyingContract.balanceOf(account);
        setUserUnderlyingBalance(convertTokenAmt(underlyingBalance, pool));

        const poolUnderlyingBalance = await ercUnderlyingContract.balanceOf(pool.address);
        setPoolUnderlyingBalance(convertTokenAmt(poolUnderlyingBalance, pool));

      } else {
        const underlyingBalance = await chainConfig.provider.getBalance(account);
        setUserUnderlyingBalance(convertTokenAmt(underlyingBalance, pool));

        const poolUnderlyingBalance = await chainConfig.provider.getBalance(pool.address);
        setPoolUnderlyingBalance(convertTokenAmt(poolUnderlyingBalance, pool));
      }

      const ercPoolContract = new ethers.Contract(
        pool.poolToken,
        ERCABI,
        chainConfig.provider
      );
      const poolTokenBalance = await ercPoolContract.balanceOf(account);
      setUserPoolBalance(convertTokenAmt(poolTokenBalance, pool));


      let poolPrice;
      if (pool.native) {
        const poolContract = new ethers.Contract(
          pool.address,
          NativeBitBitWin.abi,
          chainConfig.provider
        );
        poolPrice = await poolContract.getPoolPrice(0);
      } else {
        const poolContract = new ethers.Contract(
          pool.address,
          ERCBitBitWin.abi,
          chainConfig.provider
        );
        poolPrice = await poolContract.getPoolPrice();
      }
      setPoolPrice(Number(poolPrice) / 10000);
    }
  }
  useEffect(() => {
    refreshBalance();
  }, [pool, selectedPool, account])

  return (
    <Box bg="gray.900" minH="100vh" color="white">
      <Box position="fixed" top="0" right="0" p="4" zIndex="100">
        <HStack spacing={4} alignItems="center" justify="flex-end">
          <LanguageSelector />
          {account ? (
            <WalletInfo
              account={account}
              chainId={chainId}
              onDisconnect={disconnect}
              onSwitchChain={handleSwitchChain}
              onSwitchWallet={openConnectModal}
            />
          ) : (
            <Button
              onClick={openConnectModal}
              bg="gray.800"
              color="white"
              borderWidth="1px"
              borderColor="blue.500"
              size={{ base: "sm", md: "md" }}
              px={{ base: 2, md: 4 }}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 0 20px rgba(66, 153, 225, 0.3)',
                bg: 'gray.700'
              }}
            >
              {t('pool.connect')}
            </Button>
          )}
        </HStack>
      </Box>

      <Container maxW="container.xl" py={{ base: 16, md: 10 }}>
        <VStack spacing={8} align="stretch">
          <Heading
            as="h1"
            size="2xl"
            bgGradient="linear(to-r, cyan.400, blue.500, purple.600)"
            bgClip="text"
            fontWeight="extrabold"
            textAlign="center"
            cursor="pointer"
            onClick={() => navigate('/')}
            _hover={{
              opacity: 0.8,
              transform: 'scale(1.02)',
            }}
            transition="all 0.2s"
          >
            {t('pool.title')}
          </Heading>

          <Box>
            <PoolSelector
              chainConfig={chainConfig}
              selectedPool={selectedPool}
              onPoolSelect={updateSelectedPool}
            />
          </Box>

          {selectedPool && (
            <>
              <Box
                p={6}
                borderRadius="xl"
                bg="gray.800"
                borderWidth="1px"
                borderColor="blue.500"
                boxShadow="0 0 20px rgba(66, 153, 225, 0.3)"
              >
                <StatGroup>
                  <Stat>
                    <StatLabel fontSize="md" color="gray.400">{t('pool.balance')}</StatLabel>
                    <StatNumber
                      fontSize="3xl"
                      bgGradient="linear(to-r, cyan.400, blue.500)"
                      bgClip="text"
                    >
                      {userPoolBalance} {pool?.poolTokenSymbol}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel fontSize="md" color="gray.400">{t('pool.underlying')}</StatLabel>
                    <StatNumber
                      fontSize="3xl"
                      bgGradient="linear(to-r, cyan.400, blue.500)"
                      bgClip="text"
                    >
                      {userUnderlyingBalance} {pool?.name}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel fontSize="md" color="gray.400">{t('pool.price')}</StatLabel>
                    <StatNumber
                      fontSize="3xl"
                      bgGradient="linear(to-r, cyan.400, blue.500)"
                      bgClip="text"
                    >
                      {poolPrice}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel fontSize="md" color="gray.400">{t('pool.underlyingBalance')}</StatLabel>
                    <StatNumber
                      fontSize="3xl"
                      bgGradient="linear(to-r, cyan.400, blue.500)"
                      bgClip="text"
                    >
                      {poolUnderlyingBalance} {pool?.name}
                    </StatNumber>
                  </Stat>
                </StatGroup>
              </Box>

              <HStack spacing={4} justify="center">
                <Button
                  colorScheme="purple"
                  size="lg"
                  onClick={onDepositOpen}
                  isDisabled={!account}
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: '0 0 20px rgba(159, 122, 234, 0.3)',
                  }}
                >
                  {t('pool.deposit')}
                </Button>
                <Button
                  colorScheme="purple"
                  size="lg"
                  onClick={onWithdrawOpen}
                  isDisabled={!account}
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: '0 0 20px rgba(159, 122, 234, 0.3)',
                  }}
                >
                  {t('pool.withdraw')}
                </Button>
              </HStack>

              <DepositModal
                isOpen={isDepositOpen}
                onClose={onDepositClose}
                pool={pool}
                balance={userUnderlyingBalance}
                walletProvider={walletProvider}
                account={account}
                chainConfig={chainConfig}
                refreshBalance={refreshBalance}
              />

              <WithdrawModal
                isOpen={isWithdrawOpen}
                onClose={onWithdrawClose}
                pool={pool}
                balance={userPoolBalance}
                walletProvider={walletProvider}
                account={account}
                chainConfig={chainConfig}
                refreshBalance={refreshBalance}
              />
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
}
